import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  HomeIcon as HomeOutline,
  ArchiveBoxIcon as ArchiveOutline,
  UserIcon as UserOutline,
  MagnifyingGlassCircleIcon as SearchOutline,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";

import {
  HomeIcon as HomeSolid,
  ArchiveBoxIcon as ArchiveSolid,
  UserIcon as UserSolid,
  MagnifyingGlassCircleIcon as SearchSolid,
} from "@heroicons/react/24/solid";

import { db, auth } from "./firebase";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  doc,
  getDoc
} from "firebase/firestore";
import AggiungiProdotto from "./AggiungiProdotto";
import { validateTags, getTagCount, validateWishlistName } from './tagValidation';

export default function BottomNavBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [isPlusHovered, setIsPlusHovered] = useState(false);
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const [aggiungiProdottoAttivo, setAggiungiProdottoAttivo] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userWishlists, setUserWishlists] = useState([]);
  const [selectedWishlistId, setSelectedWishlistId] = useState(null);
  const [showAddProductComponent, setShowAddProductComponent] = useState(false);
  const [showCreateWishlistModal, setShowCreateWishlistModal] = useState(false);
  const [newWishlistName, setNewWishlistName] = useState("");
  const [newWishlistTags, setNewWishlistTags] = useState("");
  const [newWishlistEventName, setNewWishlistEventName] = useState("");
  const [newWishlistDate, setNewWishlistDate] = useState("");
  const [tagError, setTagError] = useState("");

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUserId(user ? user.uid : null);
    });
    return () => unsubscribe();
  }, []);

  const openModal = () => setShowModal(true);

  const closeModal = () => {
    setShowModal(false);
    setAggiungiProdottoAttivo(false);
    setShowCreateWishlistModal(false);
    setNewWishlistName("");
    setNewWishlistTags("");
    setNewWishlistEventName("");
    setNewWishlistDate("");
    setTagError("");
  };

  const handleNavigate = async (path) => {
    if (path === "crea-wishlist") {
      setShowCreateWishlistModal(true);
      return;
    }
    
    closeModal();
    if (path === "aggiungi-prodotto") {
      if (!currentUserId) return;
      const q = query(
        collection(db, "wishlists"),
        where("userId", "==", currentUserId),
        where("archived", "==", false)
      );
      const snap = await getDocs(q);
      const lists = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUserWishlists(lists);
      setAggiungiProdottoAttivo(true);
      setShowModal(true);
    } else {
      // Pulisci sessionStorage per evitare ripristino scroll
      sessionStorage.removeItem('fromScroll');
      sessionStorage.removeItem('fromPage');
      // Scroll immediato a inizio pagina
      window.scrollTo(0, 0);
      navigate(path, { 
        state: { 
          fromBottomNav: true,
          scrollToTop: true 
        } 
      });
    }
  };

  const handleSelectWishlist = (wishlistId) => {
    closeModal();
    setSelectedWishlistId(wishlistId);
    setShowAddProductComponent(true);
  };

  const handleCreateWishlist = async () => {
    if (!auth.currentUser) return;
    const user = auth.currentUser;

    try {
      // Controllo limite totale lifetime (50 per tutti)
      const totalWishlistsQuery = query(
        collection(db, "wishlists"),
        where("userId", "==", user.uid)
      );
      const totalSnapshot = await getDocs(totalWishlistsQuery);
      const totalWishlists = totalSnapshot.size;

      if (totalWishlists >= 50) {
        setTagError(t('errors.lifetimeLimitReached'));
        return;
      }

      // Controllo limite attive per utenti non Premium (10)
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const isPremium = userDoc.exists() ? userDoc.data().isPremium || false : false;

      if (!isPremium) {
        const activeWishlistsQuery = query(
          collection(db, "wishlists"),
          where("userId", "==", user.uid),
          where("archived", "==", false)
        );
        const activeSnapshot = await getDocs(activeWishlistsQuery);
        const activeWishlists = activeSnapshot.size;

        if (activeWishlists >= 10) {
          setTagError(t('errors.wishlistLimitActive'));
          return;
        }
      }

      // Validate wishlist name for Apple App Store compliance
      const nameValidation = validateWishlistName(newWishlistName);
      if (!nameValidation.isValid) {
        setTagError(t(`errors.${nameValidation.error}`));
        return;
      }

      const tagsArray = newWishlistTags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag);

      // Validazione completa dei tag inclusa moderazione
      const tagValidation = validateTags(newWishlistTags);
      if (!tagValidation.isValid) {
        setTagError(t(`errors.${tagValidation.error}`));
        return;
      }

      if (tagsArray.length > 5) {
        setTagError(t('errors.tagLimit'));
        return;
      }

      const eventDate = newWishlistDate ? new Date(newWishlistDate + 'T23:59:59') : null;

      const wishlistData = {
        name: newWishlistName.trim(),
        eventName: newWishlistEventName.trim() || "",
        tags: tagsArray,
        eventDate: eventDate ? eventDate.toISOString() : null,
        userId: auth.currentUser.uid,
        isPublic: false,
        likes: [],
        products: [],
        createdAt: serverTimestamp(),
        archived: false
      };

      const docRef = await addDoc(collection(db, "wishlists"), wishlistData);
      
      // Reset form and close modal
      setNewWishlistName("");
      setNewWishlistTags("");
      setNewWishlistDate("");
      setNewWishlistEventName("");
      setShowCreateWishlistModal(false);
      setShowModal(false);
      setTagError("");

      // Navigate to home to show the newly created wishlist
      navigate("/", { state: { fromBottomNav: true, scrollToTop: true } });
      
    } catch (error) {
      console.error("❌ Errore nella creazione della wishlist:", error);
      setTagError(`${t('errors.createWishlistError')}: ${error.message}`);
    }
  };

  return (
    <>
      <div style={styles.navbar}>
        <div
          onClick={() => handleNavigate("/")}
          style={isActive("/") ? styles.active : styles.icon}
          onMouseEnter={() => setHoveredIcon("home")}
          onMouseLeave={() => setHoveredIcon(null)}
        >
          {isActive("/") ? (
            <HomeSolid style={styles.iconSvg} />
          ) : (
            <HomeOutline
              style={{
                ...styles.iconSvg,
                color: hoveredIcon === "home" ? "#fff" : "#aaa",
              }}
            />
          )}
        </div>

        <div
          onClick={() => handleNavigate("/feed")}
          style={isActive("/feed") ? styles.active : styles.icon}
          onMouseEnter={() => setHoveredIcon("feed")}
          onMouseLeave={() => setHoveredIcon(null)}
        >
          {isActive("/feed") ? (
            <SearchSolid style={styles.iconSvg} />
          ) : (
            <SearchOutline
              style={{
                ...styles.iconSvg,
                color: hoveredIcon === "feed" ? "#fff" : "#aaa",
              }}
            />
          )}
        </div>

        <div
          onClick={openModal}
          style={{
            ...styles.icon,
            color: isPlusHovered ? "#fff" : "#aaa",
          }}
          onMouseEnter={() => setIsPlusHovered(true)}
          onMouseLeave={() => setIsPlusHovered(false)}
        >
          <PlusCircleIcon style={styles.plusIconSvg} />
        </div>

        <div
          onClick={() => handleNavigate("/archivio")}
          style={isActive("/archivio") ? styles.active : styles.icon}
          onMouseEnter={() => setHoveredIcon("archive")}
          onMouseLeave={() => setHoveredIcon(null)}
        >
          {isActive("/archivio") ? (
            <ArchiveSolid style={styles.iconSvg} />
          ) : (
            <ArchiveOutline
              style={{
                ...styles.iconSvg,
                color: hoveredIcon === "archive" ? "#fff" : "#aaa",
              }}
            />
          )}
        </div>

        <div
          onClick={() => handleNavigate("/profile")}
          style={isActive("/profile") ? styles.active : styles.icon}
          onMouseEnter={() => setHoveredIcon("profile")}
          onMouseLeave={() => setHoveredIcon(null)}
        >
          {isActive("/profile") ? (
            <UserSolid style={styles.iconSvg} />
          ) : (
            <UserOutline
              style={{
                ...styles.iconSvg,
                color: hoveredIcon === "profile" ? "#fff" : "#aaa",
              }}
            />
          )}
        </div>
      </div>

      {showModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.6)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          overflow: "hidden"
        }} onClick={closeModal}>
          <div style={{
            background: "#fff",
            padding: "20px",
            borderRadius: "16px",
            width: "90%",
            maxWidth: "320px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
            textAlign: "center",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxHeight: "80vh",
            overflowY: "auto"
          }} onClick={(e) => e.stopPropagation()}>
            {!aggiungiProdottoAttivo ? (
              <>
                <button
                  onClick={() => handleNavigate("crea-wishlist")}
                  style={styles.modalButton}
                >
                  {t('navigation.createWishlist')}
                </button>
                <button
                  onClick={() => handleNavigate("aggiungi-prodotto")}
                  style={styles.modalButton}
                >
                  {t('wishlist.addProduct')}
                </button>
                <button onClick={closeModal} style={styles.cancelButton}>
                  {t('common.cancel')}
                </button>
              </>
            ) : (
              <>
                <h3 style={{ marginBottom: "10px" }}>{t('wishlist.addToWishlist')}</h3>
                {userWishlists.map(w => (
                  <button
                    key={w.id}
                    style={styles.modalButton}
                    onClick={() => handleSelectWishlist(w.id)}
                  >
                    {w.name}
                  </button>
                ))}
                <button onClick={closeModal} style={styles.cancelButton}>
                  {t('common.cancel')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showAddProductComponent && selectedWishlistId && (
        <AggiungiProdotto
          wishlistId={selectedWishlistId}
          onClose={() => {
            setShowAddProductComponent(false);
            setSelectedWishlistId(null);
          }}
        />
      )}

      {showCreateWishlistModal && (
        <div style={styles.createWishlistOverlay} onClick={() => setShowCreateWishlistModal(false)}>
          <div
            style={styles.createWishlistModal}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: "24px", fontSize: "20px", fontWeight: "600" }}>{t('wishlist.create')}</h2>

            <input
              value={newWishlistName}
              onChange={e => setNewWishlistName(e.target.value)}
              placeholder={t('wishlist.name')}
              style={styles.input}
            />
            <div>
              <input
                type="text"
                placeholder={t('wishlist.tags')}
                value={newWishlistTags}
                onChange={(e) => {
                  setNewWishlistTags(e.target.value);
                  setTagError("");
                }}
                style={{
                  ...styles.input,
                  marginBottom: "4px",
                  borderColor: tagError ? "#ff4444" : "#ccc"
                }}
              />
              <div style={{ fontSize: "12px", color: getTagCount(newWishlistTags) > 5 ? "#ff4444" : "#666", marginBottom: "10px" }}>
                {getTagCount(newWishlistTags)}/5 tag
              </div>
              {tagError && (
                <div style={{ color: "#d00", fontSize: "13px", marginBottom: "10px" }}>
                  {tagError}
                </div>
              )}
            </div>
            <input
              value={newWishlistEventName}
              onChange={e => setNewWishlistEventName(e.target.value)}
              placeholder={t('wishlist.eventName')}
              style={styles.input}
            />
            <input
              type="date"
              value={newWishlistDate}
              onChange={e => setNewWishlistDate(e.target.value)}
              style={styles.input}
            />

            <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "24px", width: "100%" }}>
              <button onClick={handleCreateWishlist} style={styles.confirmBtn}>{t('wishlist.create')}</button>
              <button onClick={() => setShowCreateWishlistModal(false)} style={styles.cancelBtn}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  navbar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "70px",
    backgroundColor: "#111",
    borderTop: "1px solid #333",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "flex-start",
    paddingTop: "8px",
    zIndex: 1000,
  },
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "color 0.2s ease",
  },
  active: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#fff",
    transition: "color 0.2s ease",
  },
  iconSvg: {
    width: "30px",
    height: "30px",
    strokeWidth: 1.6,
  },
  plusIconSvg: {
    width: "30px",
    height: "30px",
    strokeWidth: 1.6,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1001,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: "Poppins, sans-serif",
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "24px",
    width: "90%",
    maxWidth: "320px",
    textAlign: "center",
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
  },
  modalButton: {
    backgroundColor: "#111",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "12px",
    width: "100%",
    fontSize: "14px",
    marginBottom: "12px",
    cursor: "pointer",
  },
  cancelButton: {
    backgroundColor: "#eee",
    color: "#111",
    border: "none",
    borderRadius: "8px",
    padding: "10px",
    width: "100%",
    fontSize: "14px",
    cursor: "pointer",
  },
  createWishlistOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999
  },
  createWishlistModal: {
    background: "#fff",
    borderRadius: "14px",
    width: "95%",
    maxWidth: "400px",
    maxHeight: "85vh",
    overflowY: "auto",
    padding: "28px",
    boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
    fontFamily: "Poppins, sans-serif",
    color: "#000",
    animation: "fadeIn 0.3s ease-in-out",
    zIndex: 10000,
    position: "relative"
  },
  input: {
    width: "100%",
    padding: "14px",
    marginBottom: "16px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    background: "#fff",
    boxSizing: "border-box"
  },
  confirmBtn: {
    background: "#000",
    color: "#fff",
    border: "none",
    padding: "16px",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    width: "48%",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  cancelBtn: {
    background: "#aaa",
    color: "#fff",
    border: "none",
    padding: "16px",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    width: "48%",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
};